import { Eye } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FormSection } from '../types/formBuilder';
import { saveModelConfiguration, loadModelConfiguration } from '../utils/modelConfigSaver';
import { parseAdminStaffSession } from '../utils/adminSession';
import { FormBuilder } from '../components/admin';
import { FormPreviewModal } from '../components/admin/FormPreviewModal';
import { FieldSettingsModal } from '../components/admin/FieldSettingsModal';
import { JsonAIModal } from '../components/admin/JsonAIModal';
import { OutputKeyFinderModal } from '../components/admin/OutputKeyFinderModal';
import { PageSettingsModal } from '../components/admin/PageSettingsModal';
import { createNewSection } from '../utils/formBuilderHelpers';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { AdminPageToolbar } from '../components/admin/AdminLayout';

interface ModelConfig {
  modelName: string;
  description: string;
  category: string;
  subcategory: string;
  tags: string;
  workflowPath: string;
  outputKey: string;
  outputType: string;
  iconName: string;
  iconColor: string;
  iconBg: string;
  creditCost: number;
  estimatedTime: string;
  route: string;
  sortOrder: number;
  isAvailable: boolean;
  comingSoon: boolean;
  featured: boolean;
  fal_stream_node_id: string;
  fal_polling_node_name: string;
  is_long_running: boolean;
  polling_interval_seconds: number;
  fal_client_mode: 'stream' | 'queue';
  fal_queue_webhook_url: string;
}

export const CreateModelPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editModelId = searchParams.get('edit');
  const isEditMode = !!editModelId;
  
  const [isLoadingModel, setIsLoadingModel] = useState(false);
  const [loadErrorModel, setLoadErrorModel] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [showFieldSettings, setShowFieldSettings] = useState(false);
  const [showJsonAI, setShowJsonAI] = useState(false);
  const [showOutputKeyFinder, setShowOutputKeyFinder] = useState(false);
  const [showPageSettings, setShowPageSettings] = useState(false);
  const [selectedField, setSelectedField] = useState<{ sectionId: string; fieldId: string } | null>(null);
  const [accessRecord, setAccessRecord] = useState<any>(null);
  const [modelConfigUuid, setModelConfigUuid] = useState<string | null>(null);
  
  const [sections, setSections] = useState<FormSection[]>([
    createNewSection(0)
  ]);

  const [modelConfig, setModelConfig] = useState<ModelConfig>({
    modelName: '',
    description: '',
    category: 'models',
    subcategory: '',
    tags: '',
    workflowPath: '',
    outputKey: '',
    outputType: 'image',
    iconName: 'Zap',
    iconColor: 'text-brand',
    iconBg: 'bg-brand/20',
    creditCost: 10,
    estimatedTime: '',
    route: '',
    sortOrder: 0,
    isAvailable: true,
    comingSoon: false,
    featured: false,
    fal_stream_node_id: '',
    fal_polling_node_name: '',
    is_long_running: false,
    polling_interval_seconds: 60,
    fal_client_mode: 'stream',
    fal_queue_webhook_url: '',
  });

  useEffect(() => {
    setAccessRecord(parseAdminStaffSession());

    const loadExistingModel = async () => {
      if (!isEditMode || !editModelId) return;
      
      setIsLoadingModel(true);
      setLoadErrorModel(null);
      
      try {
        console.log('🔄 [EDIT] Loading model for editing:', editModelId);
        const result = await loadModelConfiguration(editModelId);
        
        if (result.success && result.data) {
          const ar = parseAdminStaffSession();
          if (ar && ar.role !== 'superadmin' && result.data.hy_user_id !== ar.hy_user_id) {
            setLoadErrorModel('Access denied. You can only edit your own models.');
            return;
          }

          console.log('✅ [EDIT] Model loaded successfully:', result.data);
          
          // Store the actual UUID for use in PageSettingsModal
          setModelConfigUuid(result.data.id);
          
          // Populate model config
          setModelConfig({
            modelName: result.data.name || '',
            description: result.data.description || '',
            category: result.data.category || 'models',
            subcategory: result.data.subcategory || '',
            tags: Array.isArray(result.data.tags) ? result.data.tags.join(', ') : '',
            workflowPath: result.data.fal_endpoint || '',
            outputKey: result.data.output_mapping?.primary_key || '',
            outputType: result.data.output_type || 'image',
            iconName: result.data.icon_name || 'Zap',
            iconColor: result.data.icon_color || 'text-brand',
            iconBg: result.data.icon_bg || 'bg-brand/20',
            creditCost: result.data.credit_cost || 10,
            estimatedTime: result.data.estimated_time || '',
            isAvailable: result.data.is_available !== undefined ? result.data.is_available : true,
            comingSoon: result.data.coming_soon || false,
            featured: result.data.featured || false,
            route: result.data.route || '',
            sortOrder: result.data.sort_order || 0,
            fal_stream_node_id: result.data.fal_stream_node_id || '',
            fal_polling_node_name: result.data.fal_polling_node_name || '',
            is_long_running: result.data.is_long_running || false,
            polling_interval_seconds: result.data.polling_interval_seconds || 60,
            fal_client_mode: result.data.fal_client_mode === 'queue' ? 'queue' : 'stream',
            fal_queue_webhook_url: result.data.fal_queue_webhook_url || '',
          });
          // Populate form sections
          if (result.data.input_schema && Array.isArray(result.data.input_schema)) {
            // Convert input_schema back to form sections format
            const convertedSections = [{
              id: 'main-section',
              title: 'Model Parameters',
              fields: result.data.input_schema.map((field: any, index: number) => ({
                id: `field-${index}`,
                key: field.key || field.variable_name || '', // Add key property for consistency
                type: field.type || '',
                label: field.label || '',
                variable_name: field.key || field.variable_name || (field.type === 'file' ? 'image_url' : ''),
                additional_variables: field.additional_variables || [],
                additional_variable_types: field.additional_variable_types || undefined,
                required: field.required || false,
                placeholder: field.placeholder || '',
                description: field.description || '',
                advanced: field.advanced || false,
                defaultValue: field.defaultValue,
                hidden: field.hidden || false,
                min: field.min,
                max: field.max,
                step: field.step,
                rows: field.rows,
                options: field.options || [],
                multiple: field.multiple,
               fileLimit: field.fileLimit,
                accept: field.accept,
                imageOptions: field.imageOptions || [],
                allowCustomValue: field.allowCustomValue || false,
                customValueLabel: field.customValueLabel || '',
                customButtonLabel: field.customButtonLabel || '',
                customInputPlaceholder: field.customInputPlaceholder || '',
                imageValidationPrompt: field.imageValidationPrompt || undefined,
                enablePromptEnhancement: field.enablePromptEnhancement || false,
                systemInstructions: field.systemInstructions || undefined,
                promptBuilderConfig: field.promptBuilderConfig || undefined
              }))
            }];
            setSections(convertedSections);
          } else {
            // Keep default section if no input schema
            setSections([createNewSection(0)]);
          }
        } else {
          console.error('❌ [EDIT] Failed to load model:', result.error);
          setLoadErrorModel(result.error || 'Failed to load model configuration');
        }
      } catch (error) {
        console.error('💥 [EDIT] Exception loading model:', error);
        setLoadErrorModel(error instanceof Error ? error.message : 'Unknown error occurred');
      } finally {
        setIsLoadingModel(false);
      }
    };

    loadExistingModel();
  }, [isEditMode, editModelId]);

  const goBack = () => {
    navigate('/admin');
  };

  const handleOpenFieldSettings = (sectionId: string, fieldId: string) => {
    setSelectedField({ sectionId, fieldId });
    setShowFieldSettings(true);
  };

  const handleCloseFieldSettings = () => {
    setShowFieldSettings(false);
    setSelectedField(null);
  };

  const handleFieldUpdate = (sectionId: string, fieldId: string, updates: any) => {
    setSections(sections.map(section => 
      section.id === sectionId 
        ? {
            ...section,
            fields: section.fields.map(field => 
              field.id === fieldId ? { ...field, ...updates } : field
            )
          }
        : section
    ));
  };

  const handleJsonAIGenerate = (generatedSections: FormSection[], newModelConfig: any) => {
    setSections(generatedSections);
    setModelConfig(prev => ({
      ...prev,
      modelName: newModelConfig.modelName || '',
      description: newModelConfig.description || '',
      category: newModelConfig.category || 'models',
      tags: newModelConfig.tags || '',
      workflowPath: newModelConfig.workflowPath || '',
      outputKey: newModelConfig.outputKey || '',
      iconName: prev.iconName, // Keep existing icon settings
      iconColor: prev.iconColor,
      iconBg: prev.iconBg,
      fal_client_mode: newModelConfig.falClientMode === 'queue' ? 'queue' : 'stream',
      fal_queue_webhook_url: typeof newModelConfig.webhookUrl === 'string' ? newModelConfig.webhookUrl : prev.fal_queue_webhook_url,
    }));
    setShowJsonAI(false);
  };

  const handleSaveConfiguration = async () => {
    console.log('💾 [SAVE] Starting save configuration');
    console.log('📋 [SAVE] Model config:', modelConfig);
    console.log('🏗️ [SAVE] Sections:', sections);
    
    if (!modelConfig.modelName.trim()) {
      setSaveError('Model name is required');
      return;
    }

    if (!modelConfig.workflowPath.trim()) {
      setSaveError('Workflow path is required');
      return;
    }

    if (!modelConfig.category.trim()) {
      setSaveError('Category is required');
      return;
    }

    if (!modelConfig.outputType.trim()) {
      setSaveError('Output type is required');
      return;
    }

    if (!modelConfig.creditCost || modelConfig.creditCost <= 0) {
      setSaveError('Credit cost is required and must be greater than 0');
      return;
    }

    if (!modelConfig.estimatedTime.trim()) {
      setSaveError('Estimated time is required');
      return;
    }

    if (sections.length === 0 || sections.every(s => s.fields.length === 0)) {
      setSaveError('At least one form field is required');
      return;
    }

    // Check if any fields have no type selected
    const invalidFields = sections.flatMap(s => s.fields).filter(f => !f.type);
    if (invalidFields.length > 0) {
      setSaveError('All fields must have a type selected');
      return;
    }

    setIsSaving(true);
    setSaveError(null);

    try {
      console.log('📤 [SAVE] Calling saveModelConfiguration');
      const result = await saveModelConfiguration({
        modelConfig,
        sections,
        isUpdate: isEditMode,
        existingModelId: editModelId || undefined,
        currentUserId: accessRecord?.hy_user_id
      });

      console.log('📥 [SAVE] Save result:', result);

      if (result.success) {
        // Show success message and optionally redirect
        const action = isEditMode ? 'updated' : 'created';
        alert(`✅ Model "${modelConfig.modelName}" ${action} successfully! Model ID: ${result.modelId}`);
        
        // Clear form after successful save (only for create mode)
        if (!isEditMode) {
          setModelConfig({
            modelName: '',
            description: '',
            category: 'models',
            subcategory: '',
            tags: '',
            workflowPath: '',
            outputKey: '',
            outputType: 'image',
            iconName: 'Zap',
            iconColor: 'text-brand',
            iconBg: 'bg-brand/20',
            creditCost: 10,
            estimatedTime: '',
            route: '',
            sortOrder: 0,
            isAvailable: true,
            comingSoon: false,
            featured: false,
            fal_stream_node_id: '',
            fal_polling_node_name: '',
            is_long_running: false,
            polling_interval_seconds: 60,
            fal_client_mode: 'stream',
            fal_queue_webhook_url: '',
          });
          setSections([createNewSection(0)]);
        }
        
        console.log('🧹 [SAVE] Form cleared after successful save');
      } else {
        console.error('❌ [SAVE] Save failed:', result.error);
        setSaveError(result.error || 'Failed to save configuration');
      }
    } catch (error) {
      console.error('💥 [SAVE] Exception during save:', error);
      setSaveError(error instanceof Error ? error.message : 'Unknown error occurred');
    } finally {
      setIsSaving(false);
    }
  };

  // Get the field being edited
  const getSelectedField = () => {
    if (!selectedField) return null;
    
    const section = sections.find(s => s.id === selectedField.sectionId);
    const field = section?.fields.find(f => f.id === selectedField.fieldId);
    
    return field || null;
  };

  return (
    <div className="flex h-full min-h-0 w-full min-w-0 flex-1 flex-col overflow-hidden bg-base">
      <AdminPageToolbar
        title={isEditMode ? 'Edit Model' : 'Create Model'}
        subtitle={
          isEditMode
            ? `Editing: ${modelConfig.modelName || editModelId}`
            : 'Add new AI model configuration'
        }
      >
        <button
          type="button"
          onClick={() => setShowPreview(true)}
          className="flex items-center space-x-2 px-4 py-2 bg-accent text-surface rounded-lg font-medium hover:bg-accent/90 transition-all"
        >
          <Eye className="w-4 h-4" />
          <span className="hidden sm:inline">Preview</span>
        </button>
      </AdminPageToolbar>

      <main className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden overscroll-y-contain">
        <div className="max-w-6xl mx-auto px-4 py-4 pb-8 min-h-0">
          {/* Loading State */}
          {isLoadingModel ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center space-y-4">
                <LoadingSpinner size="lg" />
                <p className="text-th-secondary">Loading model configuration...</p>
              </div>
            </div>
          ) : loadErrorModel ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center space-y-4 max-w-md">
                <div className="text-status-error text-6xl">⚠️</div>
                <h3 className="text-xl font-bold text-th-primary">Failed to Load Model</h3>
                <p className="text-th-secondary">{loadErrorModel}</p>
                <button onClick={goBack} className="button-primary">
                  Back to Models
                </button>
              </div>
            </div>
          ) : (
            <FormBuilder
              sections={sections}
              onSectionsChange={setSections}
              onOpenPreview={() => setShowPreview(true)}
              onOpenFieldSettings={handleOpenFieldSettings}
              onOpenJsonAI={() => setShowJsonAI(true)}
              onOpenPageSettings={() => setShowPageSettings(true)}
             mode="model"
              modelConfig={modelConfig}
              onModelConfigChange={(config) => setModelConfig(config as any)}
              onSaveConfiguration={handleSaveConfiguration}
              isSaving={isSaving}
              saveError={saveError}
              isEditMode={isEditMode}
            />
          )}
        </div>
      </main>

      {/* Form Preview Modal */}
      <FormPreviewModal
        isOpen={showPreview}
        onClose={() => setShowPreview(false)}
        sections={sections}
        workflowPath={modelConfig.workflowPath}
        outputKey={modelConfig.outputKey}
      />

      {/* JSON AI Modal */}
      <JsonAIModal
        isOpen={showJsonAI}
        onClose={() => setShowJsonAI(false)}
        onGenerateForm={handleJsonAIGenerate}
      />

      {/* Field Settings Modal */}
      <FieldSettingsModal
        isOpen={showFieldSettings}
        onClose={handleCloseFieldSettings}
        field={getSelectedField()}
        sectionId={selectedField?.sectionId || null}
        onUpdate={handleFieldUpdate}
      />

      {/* Output Key Finder Modal */}
      <OutputKeyFinderModal
        isOpen={showOutputKeyFinder}
        onClose={() => setShowOutputKeyFinder(false)}
        onSelectOutputKey={(outputKey) => {
          setModelConfig(prev => ({ ...prev, outputKey }));
          setShowOutputKeyFinder(false);
        }}
      />

      {/* Page Settings Modal */}
      <PageSettingsModal
        isOpen={showPageSettings}
        onClose={() => setShowPageSettings(false)}
        modelconfigId={modelConfigUuid || editModelId || 'new-model'}
        modelName={modelConfig.modelName}
      />

      {/* Background Effects */}
      <div className="fixed inset-0 -z-10 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-brand/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-accent/10 rounded-full blur-3xl" />
      </div>
    </div>
  );
};